import {Component, OnInit} from '@angular/core';
import {AuthenticationService} from '../../../authenticationModule/services/authentication.service';
import {SelectItem} from 'primeng/api';
import {RequestHistoricalData} from '../../../generated/model/request-historical-data';
import * as _ from 'lodash';
import {EtreeService} from '../../../services/etree.service';
import {HistEtree} from '../../../models/hist-etree';
import {HistPop} from '../../../models/hist-pop';
import {formatDate} from '@angular/common';
import {HistoryRequestService} from '../../../generated/api/history-request.service';
import {BaseClientComponent} from '../../base-client.component';
import {UtilsService} from '../../../appSharedModule/services/utils.service';
import {HttpClient} from '@angular/common/http';

@Component({
  selector: 'app-request-historical-data',
  templateUrl: './request-historical-data.component.html',
  styleUrls: ['./request-historical-data.component.scss']
})
export class RequestHistoricalDataComponent extends BaseClientComponent implements OnInit {

  histEtrees: HistEtree[];
  timeZonesItems: SelectItem[];
  etreeItems: SelectItem[];
  popItems: SelectItem[];
  requestHistoricalData: RequestHistoricalData;
  loading = false;
  requestIsBeingProcessedAlertMessage = false;
  requestError = false;
  minStartDate: Date | undefined;
  maxStartDate: Date | undefined;
  minEndDate: Date | undefined;
  maxEndDate: Date | undefined;

  constructor(public utilsService: UtilsService,
              public authenticationService: AuthenticationService,
              private historyRequestService: HistoryRequestService,
              private etreeService: EtreeService,
              protected http: HttpClient) {
    super();
  }

  ngOnInit() {
    this.initCalendarLimits();
    this.loading = true;
    this.requestHistoricalData = new RequestHistoricalData();
    const response$ = this.authenticationService.waitForAuthenticationThenLaunch(() => {
      this.initializeEtreeItems();
    });
    this.subscriptions.push(response$);
    this.initializeTimeZonesItems();
  }

  private initCalendarLimits() {
    this.minStartDate = this.firstDay();
    this.minEndDate = this.firstDay();
    this.maxStartDate = this.lastDay();
    this.maxEndDate = this.lastDay();
  }

  private firstDay() {
    return new Date('2021-10-01');
  }

  private lastDay() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  }

  public changeStartDate() {
    const start = this.requestHistoricalData.start;
    if (!start) {
      this.minEndDate = this.firstDay();
      this.maxEndDate = this.lastDay();
      return;
    }
    this.minEndDate = start;
    const date = new Date(start);
    date.setMonth(date.getMonth() + 2);
    this.maxEndDate = date < this.lastDay() ? date : this.lastDay();
  }

  public changeEndDate() {
    const stop = this.requestHistoricalData.stop;
    if (!stop) {
      this.minStartDate = this.firstDay();
      this.maxStartDate = this.lastDay();
      return;
    }
    this.maxStartDate = stop;
    const date = new Date(stop);
    date.setMonth(date.getMonth() - 2);
    this.minStartDate = date > this.firstDay() ? date : this.firstDay();
  }

  private initializeEtreeItems() {
    const etreeItems = [];
    this.etreeService.getEtrees().subscribe({
      next: res => {
        this.histEtrees = res.filter(etree => etree.vvlanId);
        _.forEach(this.histEtrees, (etree: HistEtree) => {
          etreeItems.push({
            label: etree.companyName + ' - Injection: ' + etree.injection + ' - Xconnect: '
              + (etree.xConnect || 'N/A') + ' - E-tree Ref: ' + etree.etreeRef,
            value: etree.connectionPointId
          });
        });
        etreeItems.sort((a, b) => (
          a.label.localeCompare(b.label)
        ));
        if (etreeItems.length !== 0) {
          this.requestHistoricalData.connectionPointId = etreeItems[0].value;
          this.initializePopItems(etreeItems[0].value);
        }
        this.etreeItems = etreeItems;
      },
      error: () => this.utilsService.displayMessage('The E-Trees could not be loaded', 'Please try again later.', 'error')
    });
  }

  initializePopItems(code: string) {
    this.popItems = [];
    _.forEach(this.histEtrees.find(x => x.connectionPointId === code).listeningPop, (listeningPop: HistPop) => {
      this.popItems.push({
        label: listeningPop.name + ' - ' + listeningPop.shortname,
        value: listeningPop.shortname
      });
      this.loading = false;
    });
    this.requestHistoricalData.listeningPop = this.popItems[0].value;
  }

  private initializeTimeZonesItems() {
    this.timeZonesItems = [
      {label: 'UTC', value: 'UTC'},
      {label: 'America/Chicago', value: 'America/Chicago'},
      {label: 'America/New_York', value: 'America/New_York'},
      {label: 'Europe/Berlin', value: 'Europe/Berlin'},
      {label: 'Asia/Tokyo', value: 'Asia/Tokyo'},
    ];
    this.requestHistoricalData.timezone = this.timeZonesItems[0].value;
  }

  getUserCurrent() {
    return this.authenticationService.getUserCurrent();
  }

  onSubmit(historyRequestForm) {
    this.requestIsBeingProcessedAlertMessage = false;
    if (!historyRequestForm.valid) {
      return;
    } else {
      this.loading = true;
      this.addHistoryRequest();
      return false;
    }
  }

  private addHistoryRequest() {
    const selectedEtree = this.histEtrees.find(x => x.connectionPointId === this.requestHistoricalData.connectionPointId);
    this.requestHistoricalData.name = this.popItems.find(x => x.value === this.requestHistoricalData.listeningPop).label;
    const newHistoryRequest = new RequestHistoricalData();
    newHistoryRequest.companySugarId = this.requestHistoricalData.companySugarId;
    newHistoryRequest.name = selectedEtree.etreeRef + ' - from ' + selectedEtree.injection
      + ' to ' + this.requestHistoricalData.name;
    newHistoryRequest.frompop = selectedEtree.injection;
    newHistoryRequest.topop = this.requestHistoricalData.listeningPop;
    newHistoryRequest.start = this.updateDateByTimezone(this.requestHistoricalData.timezone,
      this.requestHistoricalData.start);
    newHistoryRequest.stop = this.updateDateByTimezone(this.requestHistoricalData.timezone,
      this.requestHistoricalData.stop);
    newHistoryRequest.etreeId = selectedEtree.etreeId;
    this.requestIsBeingProcessedAlertMessage = false;
    this.requestError = false;
    this.historyRequestService.postHistoryRequestCollection(newHistoryRequest).subscribe({
        next: () => {
          this.requestIsBeingProcessedAlertMessage = true;
          this.requestError = false;
          this.loading = false;
        },
        error: () => {
          this.requestError = true;
          this.requestIsBeingProcessedAlertMessage = false;
          this.loading = false;
        }
      }
    );
  }

  updateDateByTimezone(timezone: string, date: Date): Date {
    const wallTime = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes());
    const firstGuess = wallTime - this.timezoneOffset(timezone, wallTime);
    const convertedDate: any = formatDate(wallTime - this.timezoneOffset(timezone, firstGuess), 'yyyy-MM-ddTHH:mm', 'en-us', 'UTC');
    return convertedDate;
  }

  private timezoneOffset(timezone: string, time: number): number {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'
    }).formatToParts(time);
    const part = (type: string) => Number(parts.find(item => item.type === type).value);
    return Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute')) - Math.floor(time / 60000) * 60000;
  }
}


